import { useCallback, useMemo } from 'react';
import type { DateTime } from 'luxon';
import type { TimeValidations } from '@library/types/dates';
import { isTimeEqual } from '@library/utils/dateTime';

export const useCalendarContainer = ({
  handleSave,
  proposedTime,
  resetTime,
  setTimeOpen,
  time,
  validations
}: {
  handleSave: (date: DateTime) => void;
  proposedTime: DateTime;
  resetTime: () => void;
  setTimeOpen: (open: boolean) => void;
  time: DateTime;
  validations: TimeValidations;
}) => {
  const { isError, uniqueStartTimes = [] } = validations;

  const saveDisabled = useMemo(() => {
    const timeDisablingSave =
      isTimeEqual({ dateValue: proposedTime, dateToCompareAgainst: time }) &&
      uniqueStartTimes.length < 2;

    return isError || timeDisablingSave;
  }, [isError, proposedTime, time, uniqueStartTimes]);

  const handleCancel = useCallback(() => {
    resetTime();
    setTimeOpen(false);
  }, [resetTime, setTimeOpen]);

  const handleOnSave = useCallback(() => {
    handleSave(proposedTime);
  }, [handleSave, proposedTime]);

  const calendarContainerContext = useMemo(() => {
    return {
      handleCancel,
      handleSave: handleOnSave,
      saveDisabled
    };
  }, [handleCancel, handleOnSave, saveDisabled]);

  return calendarContainerContext;
};
